'use client';

import { useCallback, useMemo, useState } from 'react';

import { ChatContextButton } from '@/features/chat/components';
import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import {
  useDeleteTaskMutation,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from '@/features/tasks/api/tasksApi';
import { openTimeTrackerForTask } from '@/features/time-entries/time-tracker/timeTrackerEvents';
import { WorkspaceMemberAvatar } from '@/features/workspaces/components';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import {
  formatDueDateLabel,
  getDueDateRange,
  getTimelineStatusLabel,
} from '@/utils/tasks';
import {
  EmptyState,
  InfiniteTable,
  Meter,
  OptionsMenu,
  SecondaryButton,
  TimerIcon,
} from '@web/ui';
import {
  formatHours,
  getErrorMessage,
  notify,
  truncateWithEllipsis,
} from '@web/utils';

import { EditableTaskTitle } from './EditableTaskTitle';
import { MyTasksSubRowForm } from './MyTasksSubRowForm';
import styles from './MyTasksTable.module.scss';
import { UpdateTaskStatus } from './UpdateTaskStatus';

import type { SelectTaskStatusValue } from '@/features/tasks/components';
import type { Task, UpdateTaskPayload } from '@/features/tasks/types';
import type { TaskDueDateFilter } from '@/utils/tasks';

type MyTasksTableProps = {
  search: string;
  status: SelectTaskStatusValue;
  assigneeId: string;
  dueDateFilter: TaskDueDateFilter;
};

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getAssigneeServiceTypeId(task: Task): string {
  const primaryAssignee = getPrimaryAssignee(task);
  return (
    primaryAssignee?.service_type_id ?? primaryAssignee?.service_type?.id ?? ''
  );
}

function getAssigneeName(task: Task): string {
  const assignee = getPrimaryAssignee(task)?.user;

  if (!assignee) {
    return 'Unassigned';
  }

  const fullName =
    `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim();
  return fullName || assignee.email || 'Unknown user';
}

function getLoePercentage(task: Task) {
  return getPrimaryAssignee(task)?.actual_time_tracked_percentage ?? 0;
}

function getTaskContext(task: Task) {
  if (task.job?.name) {
    return {
      primary: task.job.name,
      secondary: task.deliverable?.name ?? 'Job task',
    };
  }

  const companyName = task.client?.company?.name;
  if (companyName) {
    return {
      primary: 'Quick Task',
      secondary: companyName,
    };
  }

  return {
    primary: 'Quick Task',
    secondary: 'Internal',
  };
}

export function MyTasksTable({
  search,
  status,
  assigneeId,
  dueDateFilter,
}: MyTasksTableProps) {
  const canEditTasks = useCanWorkspaceAction('task_management', 'edit', true);
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask, { isLoading: isDeletingTask }] = useDeleteTaskMutation();
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);

  const dueDateRange = useMemo(
    () => getDueDateRange(dueDateFilter),
    [dueDateFilter],
  );

  const { data, isLoading, isFetching, refetch } = useGetTasksQuery({
    page: 1,
    page_size: 100,
    task_type: 'all',
    query: search || undefined,
    status: status === 'all' ? undefined : status,
    exclude_status: status === 'all' ? 'archived' : undefined,
    assignee_id: assigneeId || undefined,
    due_after: dueDateRange.due_after,
    due_before: dueDateRange.due_before,
    sort_field: 'created_at',
    is_descending: true,
  });

  const taskRows = useMemo<Task[]>(() => data?.data?.results ?? [], [data]);
  const { data: serviceTypesData } = useGetServiceTypesQuery({
    page_size: 200,
    show_archived: false,
  });

  const serviceTypeLabelMap = useMemo(
    () =>
      new Map(
        (serviceTypesData?.data?.results ?? []).map((serviceType) => [
          serviceType.id,
          serviceType.label,
        ]),
      ),
    [serviceTypesData],
  );

  const handleUpdateTask = useCallback(
    async ({
      taskId,
      payload,
    }: {
      taskId: string;
      payload: UpdateTaskPayload;
    }) => {
      await updateTask({ taskId, payload }).unwrap();
      void refetch();
    },
    [refetch, updateTask],
  );

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask({ taskId }).unwrap();
        notify({ type: 'success', message: 'Task deleted.' });
        void refetch();
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [deleteTask, refetch],
  );

  const columns = useMemo(
    () => [
      {
        id: 'task_name',
        header: 'Task name',
        accessor: (row: Task) => row.title ?? '',
        sortable: true,
        minWidth: 150,
        cell: (row: Task) => (
          <div className={styles.myTasksTable__taskName}>
            <EditableTaskTitle
              taskId={row.id}
              value={row.title}
              canEdit={canEditTasks}
              className={styles.myTasksTable__taskTitle}
              onUpdated={() => {
                void refetch();
              }}
            />
            <UpdateTaskStatus taskId={row.id} value={row.status} />
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        accessor: (row: Task) => row.description ?? '',
        sortable: true,
        minWidth: 120,
        cell: (row: Task) => (
          <span className={styles.myTasksTable__description}>
            {truncateWithEllipsis(row.description ?? '', 40) ||
              'Add description'}
          </span>
        ),
      },
      {
        id: 'context',
        header: 'Context',
        accessor: (row: Task) => getTaskContext(row).primary,
        sortable: true,
        minWidth: 120,
        cell: (row: Task) => {
          const context = getTaskContext(row);
          return (
            <span className={styles.myTasksTable__context}>
              <strong>{context.primary}</strong>
              <span className={styles.myTasksTable__contextSecondary}>
                {context.secondary}
              </span>
            </span>
          );
        },
      },
      {
        id: 'utility',
        header: '',
        accessor: () => '',
        sortable: false,
        width: 48,
        cell: (row: Task) => {
          const isTimeTrackingDisabled =
            row.is_draft || row.for_time_logging === false;
          const isChatDisabled = !row.job_id;
          const timeTrackingAriaLabel = row.is_draft
            ? 'Disable for Draft tasks'
            : row.for_time_logging === false
              ? 'Disabled for time logging'
              : 'Track time';

          return (
            <span
              className={styles.myTasksTable__utilityCell}
              onClick={(event) => event.stopPropagation()}
            >
              <ChatContextButton
                target={{
                  type: 'task',
                  id: row.id,
                  label: row.title || 'Untitled task',
                  sourceJobId: row.job_id,
                  sourceJobLabel: row.job?.name ?? null,
                }}
                messageCount={row.message_count}
                unreadCount={row.unread_count}
                mentionCount={row.mention_count}
                disabled={isChatDisabled}
                disabledLabel="Chat is available for Job tasks only"
              />
              <SecondaryButton
                variant="mini"
                ariaLabel={timeTrackingAriaLabel}
                disabled={isTimeTrackingDisabled}
                onClick={() => openTimeTrackerForTask(row)}
              >
                <TimerIcon />
              </SecondaryButton>
            </span>
          );
        },
      },
      {
        id: 'assignee',
        header: 'Assignee',
        accessor: (row: Task) => getAssigneeName(row),
        sortable: true,
        minWidth: 80,
        cell: (row: Task) => {
          const assignee = getPrimaryAssignee(row)?.user;
          if (!assignee) return 'N/A';

          return (
            <span className={styles.myTasksTable__assignee}>
              <WorkspaceMemberAvatar
                size="small"
                user={assignee}
                closeOnTriggerMouseLeave
              />
            </span>
          );
        },
      },
      {
        id: 'service_type',
        header: 'Service type',
        accessor: (row: Task) =>
          getPrimaryAssignee(row)?.service_type?.label ??
          serviceTypeLabelMap.get(getAssigneeServiceTypeId(row)) ??
          '',
        sortable: true,
        minWidth: 100,
        cell: (row: Task) => (
          <span>
            {getPrimaryAssignee(row)?.service_type?.label ??
              serviceTypeLabelMap.get(getAssigneeServiceTypeId(row)) ??
              'N/A'}
          </span>
        ),
      },
      {
        id: 'due_date',
        header: 'Due date',
        accessor: (row: Task) => row.due_date ?? '',
        sortable: true,
        minWidth: 80,
        cell: (row: Task) => <span>{formatDueDateLabel(row.due_date)}</span>,
      },
      {
        id: 'loe_timeline',
        header: 'LOE / Timeline',
        accessor: (row: Task) => getPrimaryAssignee(row)?.level_of_effort ?? 0,
        sortable: true,
        minWidth: 100,
        cell: (row: Task) => (
          <div className={styles.myTasksTable__meterStack}>
            <Meter
              percentage={getLoePercentage(row)}
              label={`LOE ${formatHours(getPrimaryAssignee(row)?.tracked_hours)} / ${formatHours(getPrimaryAssignee(row)?.level_of_effort)}h`}
              tooltipContent={
                <span>
                  {`Tracked ${formatHours(getPrimaryAssignee(row)?.tracked_hours)}h of ${formatHours(getPrimaryAssignee(row)?.level_of_effort)}h`}
                </span>
              }
              ariaLabel="LOE meter"
            />

            <Meter
              percentage={row.due_date_time_percentage ?? 0}
              label={
                <span
                  className={
                    row.days_to_due !== null && row.days_to_due < 0
                      ? styles.myTasksTable__timelineAlert
                      : undefined
                  }
                >
                  {getTimelineStatusLabel(row.due_date, row.days_to_due)}
                </span>
              }
              tooltipContent={
                <span>
                  {`${formatDueDateLabel(row.due_date)} - ${getTimelineStatusLabel(row.due_date, row.days_to_due)}`}
                </span>
              }
              ariaLabel="Timeline meter"
            />
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        accessor: (row: Task) => row.id,
        sortable: false,
        minWidth: 50,
        cell: (row: Task) => {
          const actions = [
            {
              key: 'delete',
              label: 'Delete',
              tone: 'danger' as const,
              loading: isDeletingTask,
              onSelect: () => {
                void handleDeleteTask(row.id);
              },
            },
          ];

          return (
            <div className={styles.myTasksTable__cellAction}>
              <OptionsMenu ariaLabel="Task actions" actions={actions} />
            </div>
          );
        },
      },
    ],
    [
      canEditTasks,
      handleDeleteTask,
      isDeletingTask,
      refetch,
      serviceTypeLabelMap,
    ],
  );

  const isTableLoading = isLoading || isFetching;
  const showEmptyState = taskRows.length === 0 && !isTableLoading;

  return (
    <div className={styles.myTasksTable}>
      {showEmptyState ? (
        <EmptyState
          title="No tasks found."
          description="Assigned tasks will appear here."
        />
      ) : null}

      {!showEmptyState ? (
        <InfiniteTable<Task>
          tableId="my-tasks-table"
          columns={columns}
          rows={taskRows}
          virtualizedHeight="auto"
          rowHeight={100}
          expandedRowIds={expandedRowIds}
          onExpandedRowIdsChange={setExpandedRowIds}
          onLoadMore={() => {}}
          hasMore={false}
          isLoading={isTableLoading}
          skeletonRowCount={8}
          emptyMessage="No tasks found."
          getRowId={(row: Task) => row.id}
          renderRowSubComponent={(row: Task) => (
            <MyTasksSubRowForm
              key={`${row.id}:${row.updated_at}`}
              task={row}
              onUpdateTask={handleUpdateTask}
            />
          )}
          isRowCollapsible={() => true}
        />
      ) : null}
    </div>
  );
}
